import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layers } from 'lucide-react';
import type { GameMode, RoomPhase } from '@interactive-room';
import { StageBackdrop } from './StageBackdrop';
import { BRAND, PHASE_STRIP } from '../../theme/designSystem';
import { ThemeToggle } from '../ui/ThemeToggle';

export type SalaVariant = 'landing' | 'cockpit' | 'field' | 'projector';

interface SalaShellProps {
  children: React.ReactNode;
  variant?: SalaVariant;
  phase?: RoomPhase;
  mode?: GameMode | null;
  /** Sin chrome — solo contenido + backdrop */
  bare?: boolean;
}

export function SalaShell({
  children,
  variant = 'cockpit',
  phase = 'LOBBY',
  mode = null,
  bare = false,
}: SalaShellProps) {
  const location = useLocation();
  const isField = variant === 'field';
  const isLanding = variant === 'landing';

  return (
    <div
      data-phase={phase}
      data-sala-variant={isLanding ? 'landing' : isField ? 'field' : 'stage'}
      data-sala-shell={variant}
      {...(isLanding
        ? { 'data-studio-surface': 'micelio', 'data-sala-theme': 'light' }
        : {})}
      className={`relative min-h-dvh${isLanding ? ' sala-landing-shell' : ''}`}
      style={{
        background: isLanding
          ? 'var(--micelio-work, #fcfcfa)'
          : isField
            ? undefined
            : 'var(--sala-void)',
      }}
    >
      {!isLanding && (
        <StageBackdrop mode={mode} variant={isField ? 'field' : 'stage'} />
      )}
      {isLanding && (
        <div className="sala-landing-ambient" aria-hidden>
          <div className="sala-landing-ambient__wash" />
        </div>
      )}

      {!bare && variant !== 'projector' && (
        <header className="relative z-20 flex items-center justify-between px-[var(--sala-gutter)] py-4">
          <Link to="/" className="group flex items-center gap-3">
            {isLanding ? (
              <span className="sala-landing-logo" aria-hidden>
                <Layers className="h-4 w-4" strokeWidth={2.25} />
              </span>
            ) : (
              <span
                className="flex h-10 w-10 items-center justify-center rounded-xl font-display text-lg font-extrabold"
                style={{
                  background: 'var(--sala-accent)',
                  color: 'var(--sala-on-accent, var(--sala-void))',
                  boxShadow: `0 0 32px var(--sala-accent-glow)`,
                }}
              >
                S
              </span>
            )}
            <div className="hidden sm:block">
              <p className="font-display text-sm font-bold leading-none tracking-tight">
                {isLanding ? 'Sala' : BRAND.name}
              </p>
              <p className="sala-label mt-0.5">{isLanding ? 'Edusico' : BRAND.tagline}</p>
            </div>
          </Link>

          {!isLanding && (
            <nav className="flex items-center gap-1">
              {[
                { to: '/host', label: 'Consola' },
                { to: '/studio', label: 'Studio' },
                { to: '/join', label: 'Campo' },
              ].map(({ to, label }) => {
                const active = location.pathname.startsWith(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    className="rounded-lg px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider transition"
                    style={{
                      background: active ? 'var(--sala-surface-raised)' : 'transparent',
                      color: active ? 'var(--sala-accent-neon)' : 'var(--sala-ink-muted)',
                      border: active ? '1px solid var(--sala-border-strong)' : '1px solid transparent',
                    }}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>
          )}

          <div className="flex items-center gap-2">
            {isLanding && location.pathname !== '/join' && (
              <Link to="/join" className="sala-landing-join">
                Unirme con código
              </Link>
            )}
            {variant === 'cockpit' && (
              <div className="sala-kicker hidden md:flex">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: PHASE_STRIP[phase].color }}
                />
                {PHASE_STRIP[phase].label}
              </div>
            )}
            {!isLanding && <ThemeToggle compact={variant === 'field'} />}
          </div>
        </header>
      )}

      <div
        className={
          bare || variant === 'projector'
            ? 'relative z-10 min-h-dvh'
            : 'relative z-10 px-[var(--sala-gutter)] pb-[var(--sala-gutter)]'
        }
      >
        {children}
      </div>
    </div>
  );
}
